import { useState } from "react";
import { Plus, FolderKanban, Archive, ArchiveRestore, ListTodo, Pencil } from "lucide-react";
import { api } from "@/api/client";
import type { Project, Task } from "@/api/types";
import { useWorkspaceId, useSession } from "@/app/session";
import { useProjects, useClients, useTasks, invalidateWorkspace } from "@/features/data";
import { Page } from "@/components/Page";
import { Button, Card, ColorDot, EmptyState, ErrorState, Field, IconButton, Input, Spinner } from "@/components/ui";
import { Select } from "@/components/Select";
import { Dialog } from "@/components/Dialog";
import { useToast, errorMessage } from "@/components/Toast";
import { PROJECT_COLORS, randomColor } from "@/lib/palette";
import { secondsToHMM, centsToInput, inputToCents } from "@/lib/format";
import { cn } from "@/lib/cn";

export function ProjectsPage() {
  const wid = useWorkspaceId();
  const { workspace } = useSession();
  const isAdmin = workspace?.role !== "member";
  const { toast } = useToast();
  const { data: projects, isLoading, isError, refetch } = useProjects(true);
  const [dialog, setDialog] = useState<{ project?: Project } | null>(null);
  const [tasksFor, setTasksFor] = useState<Project | null>(null);

  async function archive(p: Project, archived: boolean) {
    try {
      await api.patch(`/workspaces/${wid}/projects/${p.id}`, { archived });
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  const active = projects?.filter((p) => !p.archived) ?? [];
  const archived = projects?.filter((p) => p.archived) ?? [];

  return (
    <Page
      title="Projects"
      description="Engagements and budgets. Recurring work categories live as tasks inside them."
      actions={isAdmin && <Button size="sm" onClick={() => setDialog({})}><Plus className="size-4" /> New project</Button>}
    >
      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : active.length === 0 && archived.length === 0 ? (
        <EmptyState icon={<FolderKanban className="size-8" />} title="No projects yet" hint={isAdmin ? "Create your first project to start attributing time." : "An admin hasn't added any projects yet."} />
      ) : (
        <div className="space-y-6">
          <Card>
            {active.map((p, i) => (
              <ProjectRow key={p.id} p={p} last={i === active.length - 1} isAdmin={isAdmin} onArchive={archive} onEdit={() => setDialog({ project: p })} onTasks={() => setTasksFor(p)} />
            ))}
            {active.length === 0 && <p className="px-4 py-6 text-center text-sm text-faint">All projects are archived.</p>}
          </Card>
          {archived.length > 0 && (
            <div>
              <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-faint">Archived</h2>
              <Card>
                {archived.map((p, i) => (
                  <ProjectRow key={p.id} p={p} last={i === archived.length - 1} isAdmin={isAdmin} onArchive={archive} onEdit={() => setDialog({ project: p })} onTasks={() => setTasksFor(p)} />
                ))}
              </Card>
            </div>
          )}
        </div>
      )}
      {dialog && <ProjectDialog project={dialog.project} onClose={() => setDialog(null)} />}
      {tasksFor && <TasksDialog project={tasksFor} onClose={() => setTasksFor(null)} />}
    </Page>
  );
}

function ProjectRow({
  p,
  last,
  isAdmin,
  onArchive,
  onEdit,
  onTasks,
}: {
  p: Project;
  last: boolean;
  isAdmin: boolean;
  onArchive: (p: Project, a: boolean) => void;
  onEdit: () => void;
  onTasks: () => void;
}) {
  const { data: clients } = useClients(true);
  const client = clients?.find((c) => c.id === p.client_id);
  return (
    <div className={cn("flex items-center gap-3 px-4 py-3", !last && "border-b border-line", p.archived && "opacity-60")}>
      <ColorDot color={p.color} className="size-3" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{p.name}</p>
        {client && <p className="truncate text-xs text-muted">{client.name}</p>}
      </div>
      {p.hourly_rate_cents != null && <span className="mono text-xs tabular-nums text-faint">{centsToInput(p.hourly_rate_cents)}/h</span>}
      {p.billable_default && <span className="text-xs text-accent">$</span>}
      <span className="mono w-16 text-right text-sm tabular-nums text-muted">{secondsToHMM(p.tracked_sec)}</span>
      {isAdmin && (
        <>
          <Button variant="ghost" size="sm" onClick={onTasks} className="gap-1.5 text-muted">
            <ListTodo className="size-4" /> Tasks
          </Button>
          <IconButton label="Edit" onClick={onEdit}><Pencil className="size-4" /></IconButton>
          <IconButton label={p.archived ? "Unarchive" : "Archive"} onClick={() => onArchive(p, !p.archived)}>
            {p.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
          </IconButton>
        </>
      )}
    </div>
  );
}

function ProjectDialog({ project, onClose }: { project?: Project; onClose: () => void }) {
  const wid = useWorkspaceId();
  const { workspace } = useSession();
  const { toast } = useToast();
  const { data: clients } = useClients();
  const editing = !!project;
  const [name, setName] = useState(project?.name ?? "");
  const [clientId, setClientId] = useState(project?.client_id ?? "");
  const [color, setColor] = useState(project?.color ?? randomColor());
  const [billable, setBillable] = useState(project?.billable_default ?? false);
  const [rate, setRate] = useState(centsToInput(project?.hourly_rate_cents));
  const [busy, setBusy] = useState(false);

  async function submit() {
    const rateCents = inputToCents(rate);
    if (rateCents === undefined) {
      toast("Enter the hourly rate as a number, e.g. 950", "error");
      return;
    }
    setBusy(true);
    try {
      const body = {
        name,
        client_id: clientId || null,
        color,
        billable_default: billable,
        hourly_rate_cents: rateCents,
      };
      if (editing) await api.patch(`/workspaces/${wid}/projects/${project!.id}`, body);
      else await api.post(`/workspaces/${wid}/projects`, body);
      invalidateWorkspace(wid);
      onClose();
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={editing ? "Edit project" : "New project"}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Website redesign" />
        </Field>
        <Field label="Client">
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full">
            <option value="">No client</option>
            {clients?.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
        </Field>
        <Field label="Color">
          <div className="flex flex-wrap gap-2">
            {PROJECT_COLORS.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} className={cn("size-6 rounded-full ring-offset-2 ring-offset-surface", color === c && "ring-2 ring-accent")} style={{ backgroundColor: c }} aria-label={c} />
            ))}
          </div>
        </Field>
        <div className="flex items-end gap-4">
          <Field label={`Hourly rate (${workspace?.currency ?? "SEK"})`} hint="Overrides the workspace default.">
            <Input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="—" className="w-32" inputMode="decimal" />
          </Field>
          <label className="mb-6 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} className="accent-[var(--c-accent)]" />
            Billable by default
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" loading={busy} disabled={!name}>{editing ? "Save" : "Create"}</Button>
        </div>
      </form>
    </Dialog>
  );
}

function TasksDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const { data: tasks = [], isLoading } = useTasks(project.id);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${wid}/projects/${project.id}/tasks`, { name: name.trim() });
      setName("");
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function rename(t: Task, next: string) {
    setEditingId(null);
    const trimmed = next.trim();
    if (!trimmed || trimmed === t.name) return;
    try {
      await api.patch(`/workspaces/${wid}/tasks/${t.id}`, { name: trimmed });
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  async function archive(t: Task, archived: boolean) {
    try {
      await api.patch(`/workspaces/${wid}/tasks/${t.id}`, { archived });
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Tasks · ${project.name}`} description="Recurring work categories inside this project.">
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-6"><Spinner /></div>
        ) : tasks.length === 0 ? (
          <p className="py-4 text-center text-sm text-faint">No tasks yet — add the first below.</p>
        ) : (
          <div className="max-h-64 overflow-auto rounded-lg border border-line">
            {tasks.map((t, i) => (
              <div key={t.id} className={cn("flex items-center gap-2 px-3 py-2", i < tasks.length - 1 && "border-b border-line", t.archived && "opacity-60")}>
                {editingId === t.id ? (
                  <input
                    autoFocus
                    defaultValue={t.name}
                    onBlur={(e) => rename(t, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    className="h-7 flex-1 rounded border border-accent bg-canvas px-2 text-sm text-ink focus:outline-none"
                  />
                ) : (
                  <span className="flex-1 truncate text-sm text-ink">{t.name}</span>
                )}
                <IconButton label="Rename" onClick={() => setEditingId(t.id)} className="size-7"><Pencil className="size-3.5" /></IconButton>
                <IconButton label={t.archived ? "Unarchive" : "Archive"} onClick={() => archive(t, !t.archived)} className="size-7">
                  {t.archived ? <ArchiveRestore className="size-3.5" /> : <Archive className="size-3.5" />}
                </IconButton>
              </div>
            ))}
          </div>
        )}
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a task…" />
          <Button type="submit" size="sm" loading={busy} disabled={!name.trim()}><Plus className="size-4" /></Button>
        </form>
      </div>
    </Dialog>
  );
}
